<template>
  <div class="container mx-auto p-4 md:p-6">
    <div class="grid md:grid-cols-4 gap-4">

      <!-- ── Main content (left, 3 cols) ── -->
      <div class="md:col-span-3">

        <!-- Active filter indicator -->
        <div v-if="activeOrderId" class="mb-4 flex items-center gap-2 flex-wrap">
          <span class="text-sm text-gray-500">{{ $t('showing') || 'Showing' }}:</span>
          <span class="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-md">
            {{ activeOrderName }}
            <button @click="clearFilter" class="ml-1 hover:text-blue-600 font-bold">✕</button>
          </span>
        </div>

        <!-- Loading skeletons -->
        <div v-if="loading" class="space-y-4">
          <div v-for="i in 5" :key="i" class="p-4 bg-white rounded shadow">
            <div class="flex flex-col sm:flex-row">
              <div class="w-full sm:w-[150px] h-[100px] mb-3 sm:mb-0 sm:mr-4 bg-gray-200 rounded-md animate-pulse"></div>
              <div class="flex-1">
                <div class="h-6 bg-gray-200 rounded w-3/4 mb-2 animate-pulse"></div>
                <div class="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                <div class="h-4 bg-gray-200 rounded w-1/2 mt-1 animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-else-if="filteredSectors.length === 0" class="flex flex-col items-center justify-center p-10 bg-white rounded-lg shadow min-h-[250px]">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-14 w-14 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 class="text-lg font-semibold text-gray-500 mb-1">{{ $t('construct') }}</h2>
          <p class="text-gray-400 text-sm text-center">{{ $t('constructMessage') }}</p>
          <button v-if="activeOrderId" @click="clearFilter" class="mt-3 text-sm text-blue-600 hover:underline">
            {{ $t('clearFilter') || 'Show all' }}
          </button>
        </div>

        <!-- Structure list -->
        <div v-else>
          <!-- Navigation overlay -->
          <div v-if="isNavigating" class="navigation-overlay">
            <div class="loading-spinner"></div>
            <p class="loading-text">{{ $t('loading') }}</p>
          </div>

          <div
            v-for="structure in paginatedSectors"
            :key="structure.id"
            class="mb-4 p-4 bg-white rounded shadow hover:shadow-md transition-shadow"
          >
            <a href="#" class="block" @click.prevent="handleNavigation(structure)">
              <div class="flex flex-col sm:flex-row">
                <div class="w-full sm:w-[150px] h-[100px] mb-3 sm:mb-0 sm:mr-4 flex-shrink-0">
                  <img
                    :src="structure.photo"
                    class="w-full h-full object-cover rounded-md bg-gray-200"
                    alt=""
                    @error="handleImageError"
                  />
                </div>
                <div class="flex-1 text-left">
                  <h2 class="text-lg md:text-xl font-semibold text-gray-800">{{ structure.name }}</h2>
                  <p v-if="isValidDescription(structure.description)" class="text-gray-600 text-sm md:text-base line-clamp-2">
                    {{ structure.description }}
                  </p>
                  <div class="mt-2 text-right">
                    <span class="inline-block text-blue-600 hover:text-blue-800 text-sm font-medium">
                      {{ $t('readMore') }} →
                    </span>
                  </div>
                </div>
              </div>
            </a>
          </div>

          <!-- Pagination -->
          <div v-if="totalPages > 1" class="pagination-controls mt-6">
            <button @click="goToPage(currentPage - 1)" :disabled="currentPage === 1" class="pagination-btn">{{ $t('previous') }}</button>
            <template v-for="(page, index) in visiblePages" :key="index">
              <button v-if="typeof page === 'number'" @click="goToPage(page)" :class="['pagination-btn', { active: currentPage === page }]">{{ page }}</button>
              <span v-else class="pagination-ellipsis">...</span>
            </template>
            <button @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages" class="pagination-btn">{{ $t('next') }}</button>
          </div>
        </div>
      </div>

      <!-- ── Right filter sidebar (1 col) ── -->
      <div class="md:col-span-1">
        <div class="sticky top-4">
          <div class="bg-white rounded-lg shadow overflow-hidden">
            <div class="bg-blue-800 text-white px-4 py-3">
              <span class="font-semibold text-sm tracking-wide uppercase">
                {{ $t('filterByCategory') || 'Categories' }}
              </span>
            </div>

            <!-- Loading skeleton -->
            <div v-if="loadingFilters" class="p-3 space-y-2">
              <div v-for="i in 6" :key="i" class="h-8 bg-gray-200 rounded animate-pulse"></div>
            </div>

            <!-- Filter options -->
            <div v-else class="divide-y divide-gray-100">
              <!-- All -->
              <button
                @click="clearFilter"
                :class="['w-full text-left px-4 py-3 text-sm flex items-center justify-between transition-colors',
                  !activeOrderId
                    ? 'bg-blue-50 text-blue-800 font-semibold border-l-4 border-blue-600'
                    : 'text-gray-700 hover:bg-gray-50']"
              >
                <span>{{ $t('allCategories') || 'All' }}</span>
              </button>

              <!-- One button per state order -->
              <button
                v-for="order in stateOrders"
                :key="order.id"
                @click="setFilter(order.id, order.name)"
                :class="['w-full text-left px-4 py-3 text-sm transition-colors',
                  activeOrderId === order.id
                    ? 'bg-blue-50 text-blue-800 font-semibold border-l-4 border-blue-600'
                    : 'text-gray-700 hover:bg-gray-50']"
              >
                {{ order.name }}
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { useStructureStore } from '@/stores/structure';
import { useSectorStore } from '@/stores/sector';

const BaseURL = process.env.VUE_APP_API_BASE_URL;

export default defineComponent({
  name: 'StructureList',
  setup() {
    const router = useRouter();
    const structureStore = useStructureStore();
    const sectorStore = useSectorStore();
    sectorStore._ensureCacheLoaded();
    return { router, structureStore, sectorStore };
  },
  data() {
    return {
      // allSectors = ALL public structures (unfiltered by state order)
      allSectors: [],
      stateOrders: [],   // all state orders that have structures
      activeOrderId: null,
      activeOrderName: '',
      loading: true,
      loadingFilters: true,
      isNavigating: false,
      currentPage: 1,
      itemsPerPage: 5,
    };
  },
  computed: {
    sectorId() {
      return this.sectorStore.currentSectorId;
    },
    filteredSectors() {
      if (!this.activeOrderId) return this.allSectors;
      return this.allSectors.filter(s => s.oder === this.activeOrderId);
    },
    paginatedSectors() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredSectors.slice(start, start + this.itemsPerPage);
    },
    totalPages() {
      return Math.ceil(this.filteredSectors.length / this.itemsPerPage);
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      if (total <= 7) { for (let i = 1; i <= total; i++) pages.push(i); }
      else {
        pages.push(1);
        if (current > 4) pages.push('...');
        for (let i = Math.max(2, current - 2); i <= Math.min(total - 1, current + 2); i++) pages.push(i);
        if (current < total - 3) pages.push('...');
        pages.push(total);
      }
      return pages;
    },
    calsector() { return this.allSectors; },
    currentLang() { return this.$i18n.locale || 'en'; },
  },
  watch: {
    sectorId: {
      handler(newId) {
        if (newId) this.loadData();
        else { this.allSectors = []; this.loading = false; }
      },
      immediate: true,
    },
    currentLang: {
      handler() { this.loadData(); }
    },
    activeOrderId() { this.currentPage = 1; },
  },
  methods: {
    countForOrder(orderId) {
      return this.allSectors.filter(s => s.oder === orderId).length;
    },
    setFilter(orderId, orderName) {
      if (this.activeOrderId === orderId) {
        this.clearFilter();
      } else {
        this.activeOrderId = orderId;
        this.activeOrderName = orderName;
      }
    },
    clearFilter() {
      this.activeOrderId = null;
      this.activeOrderName = '';
    },
    isValidDescription(desc) {
      return desc && desc !== 'nan' && desc !== 'none' && desc !== 'NaN' && desc.trim() !== '';
    },
    handleImageError(event) {
      event.target.src = '/assets/images/default-ministry.jpg';
    },
    async loadData() {
      this.loading = true;
      this.loadingFilters = true;
      const sectorId = this.sectorId;
      if (!sectorId) { this.loading = false; this.loadingFilters = false; return; }

      // Pre-select the clicked state order
      const clickedId = parseInt(sectorId, 10);
      this.activeOrderId = clickedId;
      this.activeOrderName = this.sectorStore.currentSectorName || '';

      await Promise.all([
        this.fetchAllPublicStructures(),
        this.fetchStateOrders(),
      ]);
    },
    async fetchAllPublicStructures() {
      this.loading = true;
      try {
        const res = await axios.get(`${BaseURL}/public-structure/`, {
          headers: { 'Accept-Language': this.currentLang }
        });
        const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        this.allSectors = data;
      } catch {
        this.allSectors = [];
      } finally {
        this.loading = false;
      }
    },
    async fetchStateOrders() {
      this.loadingFilters = true;
      try {
        const res = await axios.get(`${BaseURL}/state_orders_public/`, {
          headers: { 'Accept-Language': this.currentLang }
        });
        this.stateOrders = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      } catch {
        this.stateOrders = [];
      } finally {
        this.loadingFilters = false;
      }
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    async handleNavigation(structure) {
      if (!structure?.id || this.isNavigating) return;
      this.isNavigating = true;
      this.structureStore.clearCurrentStructure();
      try {
        const strucId = structure.id;
        const config = { headers: { 'Accept-Language': this.currentLang } };
        let structureResponse;
        try {
          structureResponse = await axios.get(`${BaseURL}/structures/${strucId}`, config);
        } catch {
          structureResponse = await axios.get(`${BaseURL}/private-structure/${strucId}`, config);
        }
        if (!structureResponse?.data) throw new Error();
        const data = structureResponse.data;
        this.structureStore.setStructureData(data);
        this.structureStore.currentStructureId = data.id;
        this.structureStore.currentStructureName = data.name;
        const [bossRes, contactRes, missionsRes, regionalsRes, personnelsRes] = await Promise.allSettled([
          axios.get(`${BaseURL}/structure_retrieve_boss/?structureId=${strucId}`, config),
          axios.get(`${BaseURL}/structure_retrieve_contacts/?structureId=${strucId}`, config),
          axios.get(`${BaseURL}/structure_retrieve_missions/?structureId=${strucId}`, config),
          axios.get(`${BaseURL}/structure_retrieve_regionals/?structureId=${strucId}`, config),
          axios.get(`${BaseURL}/state_personnels/?structureId=${strucId}`, config),
        ]);
        if (bossRes.status === 'fulfilled' && bossRes.value.data?.[0]) {
          this.structureStore.setStructureBoss(bossRes.value.data[0]);
          const bd = bossRes.value.data[0];
          if (bd.boss_structure) {
            try {
              const bc = await axios.get(`${BaseURL}/boss_retrieve_contacts/?structureId=${bd.boss_structure}`, config);
              this.structureStore.setStructureBossContact(bc.data?.[0] || {});
            } catch { /* silent */ }
          }
        }
        if (contactRes.status === 'fulfilled' && contactRes.value.data?.[0])
          this.structureStore.setStructureContact(contactRes.value.data[0]);
        if (missionsRes.status === 'fulfilled' && missionsRes.value.data)
          this.structureStore.setStructureMissions(missionsRes.value.data);
        if (regionalsRes.status === 'fulfilled' && regionalsRes.value.data)
          this.structureStore.setStructureRegionals(regionalsRes.value.data);
        if (personnelsRes.status === 'fulfilled' && personnelsRes.value.data)
          this.structureStore.setStatePersonnels(personnelsRes.value.data);
        this.router.push({
          name: 'structure-details',
          params: { lang: this.currentLang, name: encodeURIComponent(data.name) }
        });
      } catch {
        this.structureStore.setCurrentStructure(structure);
        this.router.push({
          name: 'structure-details',
          params: { lang: this.currentLang, name: encodeURIComponent(structure.name) }
        });
      } finally {
        this.isNavigating = false;
      }
    },
  },
});
</script>

<style scoped>
.pagination-controls { @apply flex justify-center items-center mt-6 gap-2 flex-wrap; }
.pagination-btn { @apply px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded border border-gray-300 transition-colors hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed; }
.pagination-btn.active { @apply bg-blue-600 text-white border-blue-600; }
.pagination-ellipsis { @apply px-2 text-gray-500; }
.line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.text-left { text-align: left; }
.navigation-overlay { @apply fixed inset-0 bg-white bg-opacity-75 flex flex-col items-center justify-center z-50; }
.loading-spinner { @apply animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-800; }
.loading-text { @apply mt-4 text-gray-600 font-medium; }
</style>